// FeatureMarquee — scrolling pill strip | icons + names from portals | Purple (student) → Pink (counselor) → Gold (school) | seamless loop via translateX(-50%)

import { useWindowWidth } from '../../hooks/useWindowWidth'
import {
  LayoutDashboard,
  GraduationCap,
  FileText,
  FileEdit,
  Activity,
  DollarSign,
  Sparkles,
  Search,
  Users,
  MessageSquare,
  Calendar,
  Send,
  UserPlus,
  BarChart3,
  TrendingUp,
  AlertTriangle,
  Star,
} from "lucide-react";

interface Feature {
  icon: React.ElementType;
  text: string;
  side: "student" | "counselor" | "college";
}

// Ordered: Student (purple) → Counselor (pink) → School (gold) → repeat
const FEATURES: Feature[] = [
  { icon: LayoutDashboard, text: "Dashboard",            side: "student"   },
  { icon: Users,           text: "Student Lists",        side: "counselor" },
  { icon: LayoutDashboard, text: "School Overview",      side: "college"   },

  { icon: GraduationCap,   text: "Colleges",             side: "student"   },
  { icon: MessageSquare,   text: "Chats",                side: "counselor" },
  { icon: TrendingUp,      text: "Batch Progress",       side: "college"   },

  { icon: FileText,        text: "Essays",               side: "student"   },
  { icon: Calendar,        text: "Calendar",             side: "counselor" },
  { icon: Star,            text: "Counselor Ratings",    side: "college"   },

  { icon: FileEdit,        text: "Supplements",          side: "student"   },
  { icon: Send,            text: "Send Message",         side: "counselor" },
  { icon: GraduationCap,   text: "Student Outcomes",     side: "college"   },

  { icon: Activity,        text: "Activities",           side: "student"   },
  { icon: UserPlus,        text: "Invite Student",       side: "counselor" },
  { icon: Calendar,        text: "Attendance Tracking",  side: "college"   },

  { icon: DollarSign,      text: "Financial Aid",        side: "student"   },
  { icon: Search,          text: "Research",             side: "counselor" },
  { icon: AlertTriangle,   text: "At-Risk Alerts",       side: "college"   },

  { icon: Sparkles,        text: "Rate My Chance",       side: "student"   },
  { icon: BarChart3,       text: "Placement Analytics",  side: "college"   },
  { icon: FileText,        text: "Parent Reporting",     side: "college"   },
];

const getPillStyles = (): string =>
  "border-white/10 text-white/60 bg-transparent hover:bg-white/5 transition-colors";

const getIconColor = (): string => "text-white/40";

const Pill: React.FC<{ feature: Feature; id: string }> = ({ feature, id }) => {
  const Icon = feature.icon;
  return (
    <div
      key={id}
      className={`flex-shrink-0 mx-1.5 px-3 py-2 rounded-full border flex items-center gap-2 ${getPillStyles()}`}
    >
      <Icon className={`w-4 h-4 ${getIconColor()}`} />
      <span className="text-sm font-medium whitespace-nowrap">{feature.text}</span>
    </div>
  );
};

const FeatureMarquee: React.FC = () => {
  const width = useWindowWidth()
  const isMobile = width < 768

  return (
    <div className="absolute bottom-0 left-0 right-0 overflow-hidden">
      {/* Fade edges */}
      <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#050508] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#050508] to-transparent z-10 pointer-events-none" />

      {/* Marquee track */}
      <div className="py-6">
        <div className="marquee-track flex" style={isMobile ? { animationDuration: '60s' } : undefined}>
          {FEATURES.map((f, i) => <Pill key={`a-${i}`} feature={f} id={`a-${i}`} />)}
          {FEATURES.map((f, i) => <Pill key={`b-${i}`} feature={f} id={`b-${i}`} />)}
        </div>
      </div>
    </div>
  );
};

export default FeatureMarquee;
